import { zodResolver } from '@hookform/resolvers/zod';
import clsx from 'clsx';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Textarea } from '@/components/ui/Textarea';
import { toastStore } from '@/components/ui/Toast';
import { useAppStore } from '@/app/store';
import { createPlanDefaults } from '@/domain/helpers';
import type {
  AvailabilityStatus,
  Plan,
  PlanColorKey,
  PlanStatus,
  PlanVisibility,
} from '@/domain/types';
import { PLAN_COLOR_HEX, PLAN_COLOR_KEYS } from '@/domain/types';
import { availabilityLabelKey, canMutatePlan, isPlanColorKey } from '@/domain/plans';
import { getMeta, setMeta } from '@/storage/db';
import styles from './PlanForm.module.css';

const planFormSchema = z.object({
  title: z.string().min(1),
  description: z.string(),
  planDate: z.string(),
  startTime: z.string().nullable().optional(),
  endTime: z.string().nullable().optional(),
  isAllDay: z.boolean(),
  visibility: z.enum(['shared', 'only_me']),
  hideDetailsFromPartner: z.boolean(),
  availabilityStatus: z.enum(['busy', 'free', 'tentative', 'none']),
  status: z.enum(['active', 'completed', 'cancelled']),
  colorKey: z.enum(['indigo', 'violet', 'rose', 'coral', 'amber', 'cyan', 'mint', 'graphite']).nullable(),
});

type PlanFormValues = z.infer<typeof planFormSchema>;

const AVAILABILITY_OPTIONS: AvailabilityStatus[] = ['none', 'busy', 'free', 'tentative'];
const STATUS_OPTIONS: PlanStatus[] = ['active', 'completed', 'cancelled'];

export function PlanForm() {
  const { t } = useTranslation();
  const session = useAppStore((s) => s.session);
  const space = useAppStore((s) => s.space);
  const plans = useAppStore((s) => s.plans);
  const selectedDate = useAppStore((s) => s.selectedDate);
  const planEditorOpen = useAppStore((s) => s.planEditorOpen);
  const editingPlanId = useAppStore((s) => s.editingPlanId);
  const savePlan = useAppStore((s) => s.savePlan);
  const deletePlan = useAppStore((s) => s.deletePlan);
  const copyPlanToDate = useAppStore((s) => s.copyPlanToDate);
  const setSelectedDate = useAppStore((s) => s.setSelectedDate);
  const closePlanEditor = useAppStore((s) => s.closePlanEditor);
  const openPlanEditor = useAppStore((s) => s.openPlanEditor);

  const editingPlan = useMemo(
    () => (editingPlanId ? plans.find((p) => p.id === editingPlanId) : undefined),
    [editingPlanId, plans],
  );

  const viewerId = session?.user.id ?? '';
  const canEdit = !editingPlan || canMutatePlan(editingPlan, viewerId);

  const defaultValues = useMemo((): PlanFormValues => {
    if (editingPlan) {
      return {
        title: editingPlan.title,
        description: editingPlan.description,
        planDate: editingPlan.planDate ?? '',
        startTime: editingPlan.startTime,
        endTime: editingPlan.endTime,
        isAllDay: editingPlan.isAllDay,
        visibility: editingPlan.visibility,
        hideDetailsFromPartner: editingPlan.hideDetailsFromPartner,
        availabilityStatus: editingPlan.availabilityStatus,
        status: editingPlan.status,
        colorKey: editingPlan.colorKey,
      };
    }
    return {
      title: '',
      description: '',
      planDate: selectedDate ?? '',
      startTime: null,
      endTime: null,
      isAllDay: true,
      visibility: 'shared',
      hideDetailsFromPartner: false,
      availabilityStatus: 'none',
      status: 'active',
      colorKey: null,
    };
  }, [editingPlan, selectedDate]);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<PlanFormValues>({
    resolver: zodResolver(planFormSchema),
    defaultValues,
  });

  useEffect(() => {
    if (!planEditorOpen) return;
    let cancelled = false;
    void (async () => {
      if (editingPlan) {
        reset(defaultValues);
        return;
      }
      let colorKey: PlanColorKey | null = null;
      try {
        const last = await getMeta<string>('last_plan_color_key');
        if (isPlanColorKey(last)) colorKey = last;
      } catch {
        // Dexie unavailable in some tests — keep null default
      }
      if (!cancelled) {
        reset({ ...defaultValues, colorKey });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [planEditorOpen, defaultValues, reset, editingPlan]);

  const visibility = watch('visibility');
  const isAllDay = watch('isAllDay');
  const colorKey = watch('colorKey');

  const onSubmit = async (values: PlanFormValues) => {
    if (!session || !space) {
      console.error('[PlanForm] Cannot save: missing session or space', {
        hasSession: Boolean(session),
        hasSpace: Boolean(space),
      });
      return;
    }
    if (editingPlan && !canMutatePlan(editingPlan, session.user.id)) return;

    const base: Plan =
      editingPlan ??
      createPlanDefaults({
        spaceId: space.id,
        ownerId: session.user.id,
        title: values.title,
        planDate: values.planDate || null,
      });

    const nextColor = values.colorKey;
    if (nextColor) {
      await setMeta('last_plan_color_key', nextColor);
    }

    // Keep original owner when editing a partner plan.
    await savePlan({
      ...base,
      ownerId: editingPlan?.ownerId ?? session.user.id,
      title: values.title.trim(),
      description: values.description,
      planDate: values.planDate || null,
      startTime: values.isAllDay ? null : values.startTime || null,
      endTime: values.isAllDay ? null : values.endTime || null,
      isAllDay: values.isAllDay,
      visibility: values.visibility as PlanVisibility,
      hideDetailsFromPartner: values.hideDetailsFromPartner,
      availabilityStatus: values.availabilityStatus,
      status: values.status,
      colorKey: nextColor,
      categoryColor: nextColor ? PLAN_COLOR_HEX[nextColor] : null,
    });
  };

  const handleDelete = async () => {
    if (!editingPlan) return;
    if (!window.confirm(t('plan.deleteConfirm'))) return;
    await deletePlan(editingPlan.id);
    closePlanEditor();
  };

  const handleDuplicate = () => {
    if (!editingPlan || !canEdit) return;
    void handleSubmit(async (values) => {
      const targetDate = values.planDate || null;
      const created = await copyPlanToDate(editingPlan.id, targetDate);
      if (!created) return;

      await savePlan({
        ...created,
        ownerId: session?.user.id ?? created.ownerId,
        title: values.title.trim(),
        description: values.description,
        planDate: targetDate,
        startTime: values.isAllDay ? null : values.startTime || null,
        endTime: values.isAllDay ? null : values.endTime || null,
        isAllDay: values.isAllDay,
        visibility: values.visibility as PlanVisibility,
        hideDetailsFromPartner: values.hideDetailsFromPartner,
        availabilityStatus: values.availabilityStatus,
        status: values.status,
        colorKey: values.colorKey,
        categoryColor: values.colorKey ? PLAN_COLOR_HEX[values.colorKey] : null,
      });

      toastStore.show({ type: 'success', message: t('plan.copied') });
      if (targetDate) setSelectedDate(targetDate);
      openPlanEditor(created.id);
    })();
  };

  const statusLabel = (status: PlanStatus) => {
    switch (status) {
      case 'active':
        return t('app.filters.active');
      case 'completed':
        return t('app.filters.completed');
      case 'cancelled':
        return t('plan.statusCancelled');
    }
  };

  return (
    <Modal
      open={planEditorOpen}
      onClose={closePlanEditor}
      title={editingPlan ? t('plan.edit') : t('plan.create')}
      footer={
        <div className={styles.footer}>
          {editingPlan && canEdit ? (
            <div className={styles.footerDanger}>
              <Button variant="danger" size="sm" onClick={() => void handleDelete()}>
                {t('common.delete')}
              </Button>
              <Button variant="secondary" size="sm" onClick={handleDuplicate}>
                {t('plan.copyToDate')}
              </Button>
            </div>
          ) : (
            <span />
          )}
          <div className={styles.footerActions}>
            <Button variant="ghost" size="sm" onClick={closePlanEditor}>
              {t('common.cancel')}
            </Button>
            {canEdit && (
              <Button
                variant="primary"
                size="sm"
                loading={isSubmitting}
                onClick={handleSubmit(onSubmit)}
              >
                {t('common.save')}
              </Button>
            )}
          </div>
        </div>
      }
    >
      {!canEdit && (
        <p className={styles.hint}>{t('plan.partnerPrivateReadonly')}</p>
      )}
      <fieldset className={styles.fieldset} disabled={!canEdit}>
      <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
        <Input
          label={t('plan.title')}
          error={errors.title?.message}
          {...register('title')}
        />

        <Textarea label={t('plan.description')} rows={3} {...register('description')} />

        <div className={styles.row}>
          <Input label={t('plan.date')} type="date" {...register('planDate')} />
          <label className={styles.checkboxRow}>
            <input type="checkbox" {...register('isAllDay')} />
            {t('plan.allDay')}
          </label>
        </div>

        {!isAllDay && (
          <div className={styles.row}>
            <Input label={t('plan.startTime')} type="time" {...register('startTime')} />
            <Input label={t('plan.endTime')} type="time" {...register('endTime')} />
          </div>
        )}

        <div className={styles.visibilityBlock}>
          <span className={styles.visibilityLabel}>{t('plan.visibility')}</span>
          <div className={styles.toggleRow} role="group" aria-label={t('plan.visibility')}>
            <button
              type="button"
              className={clsx(styles.toggleBtn, visibility === 'shared' && styles.toggleBtnActive)}
              aria-pressed={visibility === 'shared'}
              onClick={() => setValue('visibility', 'shared')}
            >
              {t('plan.shared')}
            </button>
            <button
              type="button"
              className={clsx(styles.toggleBtn, visibility === 'only_me' && styles.toggleBtnActive)}
              aria-pressed={visibility === 'only_me'}
              onClick={() => setValue('visibility', 'only_me')}
            >
              {t('plan.onlyMe')}
            </button>
          </div>
        </div>

        <label className={styles.checkboxRow}>
          <input type="checkbox" {...register('hideDetailsFromPartner')} />
          {t('plan.hideDetails')}
        </label>

        <div className={styles.row}>
          <div className={styles.rowFull}>
            <label className={styles.visibilityLabel} htmlFor="availabilityStatus">
              {t('plan.availability')}
            </label>
            <select id="availabilityStatus" className={styles.select} {...register('availabilityStatus')}>
              {AVAILABILITY_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {t(availabilityLabelKey(opt))}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.rowFull}>
            <label className={styles.visibilityLabel} htmlFor="status">
              {t('plan.status')}
            </label>
            <select id="status" className={styles.select} {...register('status')}>
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {statusLabel(opt)}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className={styles.rowFull}>
          <span className={styles.visibilityLabel}>{t('plan.categoryColor')}</span>
          <div className={styles.colorRow} role="group" aria-label={t('plan.categoryColor')}>
            {PLAN_COLOR_KEYS.map((key) => (
              <button
                key={key}
                type="button"
                className={clsx(
                  styles.colorSwatch,
                  colorKey === key && styles.colorSwatchActive,
                )}
                style={{ backgroundColor: PLAN_COLOR_HEX[key] }}
                onClick={() => setValue('colorKey', key as PlanColorKey)}
                aria-pressed={colorKey === key}
                aria-label={t(`plan.colors.${key}`)}
              />
            ))}
          </div>
        </div>
      </form>
      </fieldset>
    </Modal>
  );
}
